/**
 * IMPORTS
 */
import {create} from 'src/aggregates/conversations/api';
import {remove} from 'src/aggregates/conversations/api';
import conversationSelectors from 'src/aggregates/conversations/selectors';
import {update as updateCustomer} from 'src/aggregates/customers/api';
import customerSelectors from 'src/aggregates/customers/selectors';
import {contactTypesChannelToId} from 'src/aggregates/customers/utils';
import {loadByContact} from 'src/aggregates/timelines/api';
import {focus} from 'src/utils/focus';


/**
 * TYPES
 */
import {IContactNew} from 'src/aggregates/customers/utils.d';


/**
 * CODE
 */

/**
 *  I handle a number mismatch creating a conversation with the correct one.
 *
 *  :param account: account id
 *  :param conversation: mismatched contact's conversation id
 *  :param match: matched contact uid
 *  :param mismatch: mismatched contact uid
 *
 *  :returns: promise with nothing
 */
async function handleNumberMismatch (
    account: number,
    conversation: number,
    match: string,
    mismatch: string,
): Promise<void>
{
    // template sent to the wrong number: get contact type id
    const typeId = contactTypesChannelToId.get('whatsapp');

    // get customer
    const customer = customerSelectors.getCustomerByContact(typeId, mismatch);

    // customer not found: return
    if (customer === null)
    {
        return;
    }

    // customer found: check if matching contact already exists
    const contact = customer.contacts.find(({value}) => value === match);

    // contact is non-existent: add the matching contact to the customer
    if (contact === undefined)
    {
        (customer.contacts as IContactNew[]).push({
            type_id: contactTypesChannelToId.get('whatsapp'),
            value: match,
        });
    }

    // filter out mismatching contact
    const contacts = customer.contacts.filter(({value}) => value !== mismatch);

    // add mismatching contact as a cellphone
    (contacts as IContactNew[]).push({
        type_id: contactTypesChannelToId.get('celular'),
        value: mismatch.replace(/^55/, '').replace('@c.us', ''),
    });

    // update customer
    await updateCustomer(
        Number(customer.id),
        customer.address1,
        customer.address2,
        customer.agents.map(agent => agent.uid),
        customer.city,
        customer.code,
        customer.company,
        contacts,
        customer.document,
        customer.name,
        customer.description,
        customer.organization,
        customer.state,
        customer.zipcode,
    );

    // create new conversation
    await create(account, match, null, typeId);

    // remove conversation with mismatching contact
    await remove(conversation);

    // get matching contact's id
    const contactId = customerSelectors.getContactInCustomer(
        contactTypesChannelToId.get('whatsapp'),
        customer.id,
        match,
    )?.id ?? null;

    // get matching contact's conversation id
    const conversationId = conversationSelectors.getConversationByAccountAndUid(
        account,
        match,
    )?.id ?? null;

    // ensure focus on matching contact screen
    focus(account, contactId, conversationId, customer.id);

    // contact found: load its timeline
    if (contactId !== null)
    {
        await loadByContact(contactId);
    }
}


/**
 * EXPORTS
 */
export {
    handleNumberMismatch,
};
